import type {Document} from '@contentful/rich-text-types'
import type {PricingOption} from '../types'

export type CollectedFootnote = {
  id: string
  content: Document
}

/**
 * Builds a map of feature sys.id -> 0-based footnote index for all
 * pricing features that have a footnote, preserving document order.
 * Features sharing the same footnote entry (same sys.id) are assigned the same index.
 */
export function collectFootnoteIndexMap(options: PricingOption[]): Map<string, number> {
  const map = new Map<string, number>()
  const seenFootnoteIds = new Map<string, number>()
  let index = 0
  for (const option of options) {
    for (const plan of option.fields.plans) {
      for (const feature of plan.fields.featureList ?? []) {
        if (feature.fields.footnote) {
          const footnoteId = feature.fields.footnote.sys.id
          if (seenFootnoteIds.has(footnoteId)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            map.set(feature.sys.id, seenFootnoteIds.get(footnoteId)!)
          } else {
            map.set(feature.sys.id, index)
            seenFootnoteIds.set(footnoteId, index)
            index++
          }
        }
      }
    }
  }
  return map
}

/**
 * Collects unique footnotes from pricing options in document order.
 * Features sharing the same footnote entry (same sys.id) produce a single entry.
 * Used by OutroSection to render the footnotes list at the bottom of the page.
 */
export function collectFootnotes(options: PricingOption[] | undefined): CollectedFootnote[] {
  if (!options) return []
  const footnotes: CollectedFootnote[] = []
  const seenFootnoteIds = new Set<string>()
  for (const option of options) {
    for (const plan of option.fields.plans) {
      for (const feature of plan.fields.featureList ?? []) {
        if (feature.fields.footnote) {
          const footnoteId = feature.fields.footnote.sys.id
          if (seenFootnoteIds.has(footnoteId)) {
            continue
          }
          seenFootnoteIds.add(footnoteId)
          footnotes.push({id: footnoteId, content: feature.fields.footnote.fields.text})
        }
      }
    }
  }
  return footnotes
}
